import { h, Component } from 'preact';
import { observer } from 'mobx-preact';

const colorSchemes = {
    classic: ['#ff0000', '#00ff00', '#ffff00', '#0080ff', '#ff00ff', '#ff8000'],
    warm: ['#ff0000', '#ff4500', '#ff8c00', '#ffd700', '#ffb347', '#ff6347'],
    cool: ['#0080ff', '#00bfff', '#ffffff', '#9370db', '#87ceeb', '#e6e6fa'],
    rainbow: ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#8b00ff'],
    gold: ['#ffd700', '#ffcc00', '#ffdb58', '#f0e68c', '#daa520', '#b8860b'],
    red: ['#ff0000', '#dc143c', '#b22222', '#cd5c5c', '#ff6347', '#fa8072'],
    white: ['#ffffff', '#f5f5f5', '#fffafa', '#f0f0f0', '#fafafa', '#fffaf0'],
};

const speeds = {
    slow: 2.5,
    normal: 1.5,
    fast: 0.8,
};

const sizes = {
    small: { width: 6, height: 10, marginTop: 6 },
    medium: { width: 10, height: 14, marginTop: 4 },
    large: { width: 14, height: 20, marginTop: 2 },
};

@observer
class ChristmasLights extends Component {
    render() {
        const { position = 'top', stores, online } = this.props;
        const { appState, userStore } = stores;
        const { customer } = userStore;
        const { currentCustomer, opts = {} } = appState;

        const finalCustomer = opts?.showDyrejournal ? customer : currentCustomer;
        const { featureFlags = {} } = finalCustomer || {};

        // Only show if feature flag is enabled
        if (parseInt(featureFlags.christmasLights, 10) !== 1) {
            return null;
        }

        // Get settings from featureFlags with defaults
        const numLights = parseInt(finalCustomer?.featureFlags.christmasLightsNumLights, 10) || 20;
        const colorScheme = finalCustomer?.featureFlags.christmasLightsColorScheme || 'classic';
        const speed = finalCustomer?.featureFlags.christmasLightsSpeed || 'normal';
        const animation = finalCustomer?.featureFlags.christmasLightsAnimation || 'flicker';
        const size = finalCustomer?.featureFlags.christmasLightsSize || 'medium';
        const showWire = parseInt(finalCustomer?.featureFlags.christmasLightsShowWire, 10) === 1;

        const colors = colorSchemes[colorScheme] || colorSchemes.classic;
        const animationDuration = speeds[speed] || speeds.normal;
        const lightSize = sizes[size] || sizes.medium;

        const containerStyle = {
            position: 'fixed',
            left: 0,
            right: 0,
            height: `${lightSize.height + lightSize.marginTop + 10}px`,
            zIndex: 9999,
            pointerEvents: 'none',
            overflow: 'hidden',
            ...(position === 'top' ? { top: '-5px' } : { bottom: 0 }),
        };

        const wireStyle = {
            position: 'absolute',
            top: '8px',
            left: 0,
            right: 0,
            height: '2px',
            background: '#1a472a',
            display: showWire ? 'block' : 'none',
        };

        const lightsContainerStyle = {
            display: 'flex',
            justifyContent: 'space-around',
            padding: '0 10px',
        };

        const getAnimationStyle = (index) => {
            if (animation === 'steady') {
                return 'none';
            }

            const totalDuration = animationDuration * numLights * 0.1;

            switch (animation) {
                case 'chase': {
                    // Chase: each light turns on in sequence
                    const chaseDelay = (index * animationDuration * 0.15);
                    return `xmasChase ${animationDuration}s ease-in-out ${chaseDelay}s infinite`;
                }
                case 'twinkle': {
                    // Twinkle: random-ish delays for sparkle effect
                    const twinkleDelay = ((index * 0.37) % 1) * animationDuration;
                    return `xmasTwinkle ${animationDuration * 0.8}s ease-in-out ${twinkleDelay}s infinite`;
                }
                default: {
                    // Flicker: mostly on, rare random flicker like old tubes
                    const flickerDelay = ((index * 0.31) % 1) * 3;
                    const flickerDuration = 5 + ((index * 0.47) % 6);
                    return `xmasFlicker ${flickerDuration}s steps(1) ${flickerDelay}s infinite`;
                }
            }
        };

        return (
            <div style={containerStyle}>
                <div style={wireStyle} />
                <div style={lightsContainerStyle}>
                    {Array.from({ length: numLights }).map((_, i) => {
                        const color = colors[i % colors.length];

                        return (
                            <div
                                key={i}
                                style={{
                                    width: `${lightSize.width}px`,
                                    height: `${lightSize.height}px`,
                                    backgroundColor: color,
                                    borderRadius: '50% 50% 50% 50% / 30% 30% 70% 70%',
                                    marginTop: `${lightSize.marginTop}px`,
                                    boxShadow: `0 0 ${lightSize.width}px ${color}, 0 0 ${lightSize.width * 2}px ${color}`,
                                    animation: getAnimationStyle(i),
                                }}
                            />
                        );
                    })}
                </div>
                <style>
                    {`
                        @keyframes xmasFlicker {
                            0%, 95% { opacity: 1; filter: brightness(1.3); }
                            96% { opacity: 0.1; filter: brightness(0.2); }
                            97% { opacity: 0.8; filter: brightness(1.0); }
                            98% { opacity: 0.1; filter: brightness(0.1); }
                            99%, 100% { opacity: 1; filter: brightness(1.3); }
                        }
                        @keyframes xmasChase {
                            0%, 20%, 100% { opacity: 0.3; transform: scale(0.9); filter: brightness(0.4); }
                            10% { opacity: 1; transform: scale(1.15); filter: brightness(1.5); }
                        }
                        @keyframes xmasTwinkle {
                            0%, 100% { opacity: 1; transform: scale(1.05); filter: brightness(1.3); }
                            15% { opacity: 0.2; transform: scale(0.9); filter: brightness(0.3); }
                            30% { opacity: 1; transform: scale(1.1); filter: brightness(1.5); }
                            50% { opacity: 0.3; transform: scale(0.9); filter: brightness(0.5); }
                            70% { opacity: 1; transform: scale(1.08); filter: brightness(1.4); }
                            85% { opacity: 0.25; transform: scale(0.9); filter: brightness(0.4); }
                        }
                    `}
                </style>
            </div>
        );
    }
}

export default ChristmasLights;
